/* eslint-disable complexity, sonarjs/cognitive-complexity, max-params */
// The preview uses the production components, but all review requests stay local.
export const isPerformReviewMock = () =>
  typeof window !== 'undefined' &&
  /^\/reviews\/assignments\/[^/]+\/?$/.test(window.location.pathname) &&
  new URLSearchParams(window.location.search).get('mock') === 'true';

const copy = (value) => JSON.parse(JSON.stringify(value));
const actor = {
  id: -101,
  name: 'Alex Morgan',
  jobTitle: 'Engineering Manager'
};
const target = {
  id: -102,
  name: 'Jordan Lee',
  jobTitle: 'Senior Product Engineer',
  department: 'Product Engineering',
  careerLevel: 3,
  careerLevelName: 'Senior'
};
const options = [
  'Needs improvement',
  'Developing',
  'Meets expectations',
  'Exceeds expectations',
  'Outstanding'
].map((name, index) => ({
  id: index + 1,
  name,
  value: index + 1,
  score: index + 1,
  description: `${name}: demonstrates this behavior with increasing consistency and impact.`,
  colorHex: ['EF4444', 'EAB308', 'EAB308', '22C55E', '22C55E'][index],
  askForStrength: true,
  askForWeakness: true
}));
const objects = [
  {
    id: 101,
    name: 'Continuity Management',
    description:
      'Developing, implementing and testing a business continuity framework. [Supporting guidance](https://happy5.co/)',
    preferredSourceDescription: 'behavior',
    careerLevel: 3,
    careerLevelName: 'Senior Product Engineer',
    expectedLevel: 3,
    expectedLevelTitle: 'Proficient',
    expectedLevelDescription:
      'Independently plans and delivers work, supports colleagues, and anticipates risks.'
  },
  {
    id: 102,
    name: 'Collaboration and Communication',
    description:
      'Communicates clearly across teams and shares knowledge. This longer description demonstrates wrapping on smaller screens and provides context for the assessment.',
    preferredSourceDescription: 'expected_level',
    careerLevel: 3,
    careerLevelName: 'Senior Product Engineer',
    expectedLevel: 4,
    expectedLevelTitle: 'Advanced',
    expectedLevelDescription:
      'Leads cross-functional initiatives and coaches others to communicate effectively.'
  }
];
const questions = [
  ['comments', 'Describe the employee’s key achievements.'],
  ['multiple_choices', 'Which area had the greatest impact?'],
  ['multiple_selections', 'Which development areas should we prioritize?'],
  ['rating', 'How effectively does the employee collaborate?'],
  ['datetime', 'When should the development plan be reviewed?']
].map(([questionType, question], index) => ({
  id: 201 + index,
  questionType,
  question,
  description: 'Sample question with supporting context and optional evidence.',
  options:
    questionType === 'comments' || questionType === 'datetime'
      ? []
      : options.map((option, optionIndex) => ({
          ...option,
          option:
            questionType === 'multiple_choices'
              ? [
                  'Delivery quality',
                  'Customer impact',
                  'Team collaboration',
                  'Process improvement',
                  'Knowledge sharing'
                ][optionIndex]
              : [
                  'Communication',
                  'Technical expertise',
                  'Leadership',
                  'Planning',
                  'Mentoring'
                ][optionIndex]
        })),
  optionsLabelVisibility: true,
  needComment: ['required', 'optional', 'optional', 'optional', 'no'][index],
  minimumCommentLength: index === 0 ? 20 : 0,
  supportingDocument: index === 0 ? 'required' : 'no',
  dateSelectionLimit: 'none'
}));
const sections = [
  {
    id: 1,
    name: 'Additional review questions',
    position: 'after',
    sectionListStyleType: 'number',
    questions
  }
];
const attachment = {
  filename: 'Sample supporting evidence.txt',
  name: 'Sample supporting evidence.txt',
  size: 128,
  downloadUrl: 'data:text/plain,Sample%20review%20evidence',
  path: 'mock-evidence'
};

const makeTrack = (
  id,
  trackType,
  name,
  view = 'rating',
  mechanism = 'each'
) => ({
  id,
  trackType,
  name,
  contentName: name,
  state: 'in_progress',
  average: 3.5,
  showOption: 'score_and_label',
  objects: copy(objects),
  components: { options: copy(options) },
  answers: [
    {
      objectId: 101,
      optionId: 4,
      score: 4,
      comment: 'Consistently delivers strong results and supports the team.',
      attachments: [copy(attachment)]
    }
  ],
  suggestions: [],
  preFill: [],
  questionnaireAnswers: [],
  questionnairePreFill: [],
  questionnaireSuggestion: [],
  trackConfigId: id,
  trackConfig: {
    id,
    description:
      'Please answer the questions below and rate how well the employee demonstrates each competency.',
    autoFill: false,
    useSuggestion: true,
    suggestionSource: 'self_review',
    seeRelevantEvidence: false,
    additionalContents: []
  },
  template: {
    id,
    mechanism,
    view,
    useComment: 'required',
    minimumCommentLength: 20,
    questionsCount: 0,
    sections: [],
    sliderLabel: {
      leftLabel: 'Developing',
      centerLabel: 'Meets expectations',
      rightLabel: 'Outstanding'
    },
    config: { supportingDocument: 'optional', otherLevelDescription: true }
  }
});

export const createPerformReviewAssignment = (id) => {
  const tracks = [
    makeTrack(1, 'competencies_scoring', 'Competency Final Scoring'),
    makeTrack(2, 'values_scoring', 'Value Final Scoring — Slider', 'slider'),
    makeTrack(3, 'competencies_scoring', 'Competency Matrix', 'matrix'),
    makeTrack(4, 'values_scoring', 'Value Numeric Score', 'raw'),
    makeTrack(
      5,
      'competencies_scoring',
      'Competency Input Score',
      'rating',
      'input_score'
    ),
    makeTrack(
      6,
      'goals_scoring',
      'Goal Overall Scoring',
      'rating',
      'overall_score'
    ),
    makeTrack(
      7,
      'tasks_scoring',
      'Task Attribute Scoring',
      'rating',
      'specific_attribute'
    ),
    makeTrack(
      8,
      'competencies_recognition_feedback',
      'Competency Recognition & Feedback',
      'rating',
      'recognition_feedback'
    ),
    makeTrack(
      9,
      'values_recognition_feedback',
      'Value Recognition & Feedback',
      'rating',
      'recognition_feedback'
    ),
    makeTrack(10, 'general_questionnaire', 'Review Questionnaire'),
    makeTrack(11, 'review_aspects_scoring', 'Custom Review Aspects'),
    makeTrack(12, 'performance_rating', 'Performance Rating'),
    makeTrack(
      14,
      'behaviors_recognition_feedback',
      'Behavior Feedback — Dropdown'
    ),
    makeTrack(
      15,
      'behaviors_recognition_feedback',
      'Behavior Feedback — Multiple Selection'
    ),
    makeTrack(13, 'summary', 'Review Summary')
  ];
  tracks[0].template.sections = copy(sections);
  tracks[0].template.questionsCount = questions.length;
  tracks[0].suggestions = [
    {
      objectId: 101,
      optionId: 3,
      comment:
        'Self review: delivered a continuity plan and facilitated team workshops.',
      actor: target,
      updatedAt: '2026-09-20T08:00:00Z'
    }
  ];
  tracks[0].questionnaireAnswers = [
    {
      questionId: 201,
      comment: 'Delivered the continuity plan and mentored two colleagues.',
      attachments: [copy(attachment)]
    }
  ];
  tracks[2].showOption = 'score_only';
  tracks[3].showOption = 'label_only';
  tracks[5].answers = [
    { optionId: 4, comment: 'Strong delivery across the review period.' }
  ];
  [tracks[7], tracks[8]].forEach((track) => {
    track.answers = [];
    Object.assign(track.template, {
      minSelection: 1,
      maxSelection: 2,
      recognitionQuestion: 'What should this employee continue doing?',
      feedbackQuestion: 'What should this employee improve?'
    });
  });
  tracks[9].template.sections = copy(sections);
  tracks[9].template.questionsCount = questions.length;
  tracks[10].template.reviewAspects = [
    { id: 301, allowOverride: true, minimumCommentLength: 20 }
  ];
  tracks[10].template.config.showReviewAspectDescription = true;
  tracks[10].reviewAspects = [
    {
      id: 301,
      state: 'in_progress',
      showOption: 'score_and_label',
      showTooltip: true,
      objects: [
        {
          id: 301,
          name: 'Delivery Quality',
          description: 'Assess quality, reliability, and customer impact.',
          mechanism: 'each',
          view: 'rating',
          allowOverride: true,
          useComment: 'required',
          objectiveCategoryIds: []
        }
      ],
      components: { options: copy(options) },
      answers: [],
      preFill: [],
      suggestions: []
    }
  ];
  tracks[11].answers = {};
  tracks[11].values = copy(objects);
  tracks[11].competencies = copy(objects);
  [tracks[12], tracks[13]].forEach((track, index) => {
    track.behaviorType = 'competency';
    track.attachments = [copy(attachment)];
    track.comment = 'Continue sharing knowledge across the team.';
    track.template.config = {
      view: index === 0 ? 'dropdown' : 'multiple_selections',
      question: 'Which behaviors should this employee continue demonstrating?',
      useComment: 'required',
      minimumCommentLength: 20,
      supportingDocument: 'optional',
      minSelection: 1,
      maxSelection: 2,
      behaviorGoal: 'no'
    };
    track.template.view = track.template.config.view;
  });
  tracks[14].template.config.summaryRatingTooltipEnabled = false;
  return {
    id,
    actor: copy(actor),
    target: copy(target),
    targetPlacementId: -102,
    state: 'in_progress',
    formalReviewCycle: {
      id: -1,
      name: 'Perform Review — All UI Elements (Mock)',
      restrictedTrackNavigation: false,
      lockSubmittedAssignment: false,
      reviewedPeriodStartsAt: '2026-01-01T00:00:00Z',
      reviewedPeriodEndsAt: '2026-12-31T00:00:00Z'
    },
    formalReviewPhase: {
      id: -1,
      type: 'manager_review',
      showDueDate: true,
      sidebarEvidenceEnabled: false
    },
    endsAt: '2026-12-31T16:59:59Z',
    currentTrack: tracks[0].trackType,
    currentTrackId: tracks[0].id,
    permissions: {},
    targetPermissions: { goalSee: false },
    tracks
  };
};

let assignment;
let assignmentPath;
const success = (data) => ({
  isSuccess: true,
  error: null,
  status: 200,
  data: copy(data),
  pagination: { total: 0, totalPages: 1 }
});

export const getPerformReviewMockResponse = (
  endpoint,
  { method = 'GET', body = {} } = {}
) => {
  if (!isPerformReviewMock()) return undefined;
  const match = window.location.pathname.match(
    /\/reviews\/assignments\/([^/]+)/
  );
  if (assignmentPath !== window.location.pathname) {
    assignmentPath = window.location.pathname;
    assignment = createPerformReviewAssignment(match[1]);
  }
  const path = endpoint.replace(/^\//, '').split('?')[0];
  const base = `v1/formal_reviews/assignments/${match[1]}`;
  if (!path.startsWith(base + '/') && path !== base) return undefined;
  if (path === base) return success(assignment);
  const suffix = path.slice(base.length + 1);
  const trackId = suffix.match(/^tracks\/(\d+)/)?.[1];
  const behaviorTrackId = suffix.match(
    /^behaviors_recognition_feedback\/(\d+)/
  )?.[1];
  const aspectTrackId = suffix.match(/^review_aspects_scorings\/(\d+)/)?.[1];
  const type = suffix.split('/')[0];
  const exactTrackId = trackId || behaviorTrackId || aspectTrackId;
  const track =
    assignment.tracks.find((item) =>
      exactTrackId
        ? item.id === Number(exactTrackId)
        : item.id === assignment.currentTrackId && item.trackType === type
    ) || assignment.tracks.find((item) => item.trackType === type);
  if (method !== 'GET') {
    if (suffix === 'submit') return success({ state: 'done' });
    if (aspectTrackId) {
      const aspectId = Number(suffix.match(/review_aspects\/(\d+)/)?.[1]);
      const aspect = track?.reviewAspects.find((item) => item.id === aspectId);
      if (aspect && suffix.endsWith('answers'))
        aspect.answers = copy(body.answers || []);
      return success({ state: 'confirmed' });
    }
    if (track && suffix.endsWith('answers')) {
      const incoming = Array.isArray(body) ? body : body.answers || [];
      if (behaviorTrackId) {
        track.answers = copy(incoming);
        track.comment = body.comment;
        track.attachments = copy(body.attachments || []);
        track.state = 'done';
        return success({ state: 'done' });
      }
      if (type.endsWith('recognition_feedback')) {
        track.answers = copy(incoming);
        track.state = 'done';
        return success({ state: 'done' });
      }
      const field = trackId ? 'questionnaireAnswers' : 'answers';
      const key = trackId ? 'questionId' : 'objectId';
      incoming.forEach((answer) => {
        const existing = track[field].find((item) => item[key] === answer[key]);
        if (existing) Object.assign(existing, answer);
        else track[field].push(copy(answer));
      });
      track.state = 'done';
      const scores = track.answers
        .map(
          (answer) =>
            answer.score ??
            options.find((option) => option.id === answer.optionId)?.score
        )
        .filter((score) => typeof score === 'number');
      if (scores.length)
        track.average =
          scores.reduce((sum, score) => sum + score, 0) / scores.length;
    }
    if (track && suffix.endsWith('change_state')) track.state = body.state;
    if (track && trackId && !suffix.endsWith('answers'))
      Object.assign(track, body);
    return success({ state: body.state || 'done' });
  }
  if (suffix === 'goals_scoring' || suffix === 'tasks_scoring')
    return success({
      personal: {
        score: 4,
        totalWeight: 100,
        objectiveCount: 3,
        overallScore: options[3]
      },
      initial: { score: 3.5, name: 'Meets expectations' },
      trackState: 'done'
    });
  if (suffix.endsWith('objective_count'))
    return success({ topParentGoalCount: 0 });
  if (suffix.endsWith('other_career_levels'))
    return success(
      [
        [
          1,
          'Junior Product Engineer',
          'Basic',
          'Applies the behavior with guidance and learns from feedback.'
        ],
        [
          2,
          'Product Engineer',
          'Competent',
          'Applies the behavior independently on well-defined work.'
        ],
        [
          4,
          'Staff Product Engineer',
          'Advanced',
          'Leads cross-functional initiatives and coaches others to apply the behavior.'
        ],
        [
          5,
          'Principal Product Engineer',
          'Expert',
          'Sets direction across the organization and shapes how the behavior is practiced.'
        ]
      ].map(([careerLevel, name, title, description]) => ({
        careerLevel,
        name,
        careerLevelName: name,
        behaviorExpectedLevelTitle: title,
        behaviorExpectedLevelDescription: description
      }))
    );
  return success([]);
};

export const selectPerformReviewMockTrack = (id) => {
  const track = assignment?.tracks.find((item) => item.id == id);
  if (track) {
    assignment.currentTrackId = track.id;
    assignment.currentTrack = track.trackType;
  }
};
